import { Component, EventEmitter, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { ProcedureCode } from './../../../../core/services/app-data.service';
import { CodingBundleService } from './../../../../core/services/coding-bundle.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import * as _ from 'lodash';
import { UserService } from 'src/app/core/services/user.service';
import { GroupService } from 'src/app/core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

@Component({
  selector: 'app-coding-bundle-settings',
  templateUrl: './coding-bundle-settings.component.html',
  styleUrls: ['./coding-bundle-settings.component.scss']
})
export class CodingBundleSettingsComponent extends BaseComponent implements OnDestroy {

  @Output() codingBundlesChanged = new EventEmitter<any[]>();
  @ViewChild('addBundleDialog') addBundleDialog?: TemplateRef<any>;
  @ViewChild('importCodingBundlesDialog') importCodingBundlesDialog?: TemplateRef<any>;
  importCodingBundlesJson: string = '';
  codingBundles: any = [];
  editingBundle: any = {};

  readonly separatorKeysCodes: number[] = [13, 188]; // Enter and comma key codes
  addOnBlur = true;
  isLoading = false;

  /** A state unit (tasks/state-mode.md) bills nothing, so bundles carry no paid up front or pro bono default. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private codingBundleService: CodingBundleService,
    private groupService: GroupService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
    this.codingBundleService.codingBundles$.subscribe(bundles => {
      if (bundles) {
        this.codingBundles = _.sortBy(bundles, 'label');
      }
    });
  }

  override ngOnDestroy(): void {
    this.codingBundleService.getCodingBundlesByGroup()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  onProcedureCodesChanged(codes: ProcedureCode[], bundle: any) {
    bundle.procedureCodes = codes;
    const sub = this.codingBundleService.updateCodingBundles([bundle])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.codingBundlesChanged.emit(this.codingBundles);
          sub.unsubscribe();
        },
        (error) => {
          console.error('Error updating coding settings:', error);
          this.showToastMessage('There was an error updating your coding settings. Please try again.');
          sub.unsubscribe();
        }
      );
  }

  updatePaidUpFront(bundle: any) {
    bundle.noCharge = false;
    const sub = this.codingBundleService.updateCodingBundles([bundle])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.codingBundlesChanged.emit(this.codingBundles);
          sub.unsubscribe();
        },
        (error) => {
          console.error('Error updating coding settings:', error);
          this.showToastMessage('There was an error updating your coding settings. Please try again.');
          sub.unsubscribe();
        }
      );
  }

  updateNoCharge(bundle: any) {
    bundle.paidCash = false;
    const sub = this.codingBundleService.updateCodingBundles([bundle])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.codingBundlesChanged.emit(this.codingBundles);
          sub.unsubscribe();
        },
        (error) => {
          console.error('Error updating coding settings:', error);
          this.showToastMessage('There was an error updating your coding settings. Please try again.');
          sub.unsubscribe();
        }
      );
  }

  toggleCodeStatus(bundle: any, type: string) {
    switch (type) {
      case 'up-front':
        if (bundle.paidCash) {
          bundle.noCharge = false;
        }
        break;
      case 'no-charge':
        if (bundle.noCharge) {
          bundle.paidCash = false;
        }
        break;
    }
  }


  onDialogProcedureCodesChanged(codes: ProcedureCode[], bundle: any) {
    bundle.procedureCodes = codes;
  }

  openAddBundleDialog(bundle?: any): void {
    this.editingBundle = bundle || { id: this.codingBundles.length + 1, canDelete: true, label: '', procedureCodes: [] };
    this.dialog.open(this.addBundleDialog!, { width: '600px' });
  }

  openImportCodingBundlesDialog(event?: MouseEvent | null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.dialog.open(this.importCodingBundlesDialog!, { width: '600px' });
  }

  saveBundle() {
    this.isLoading = true;
    const index = this.codingBundles.findIndex((cb: any) => cb.id === this.editingBundle.id);
    if (!this.editingBundle.procedureCodes?.length) {
      this.isLoading = false;
      this.dialog.closeAll();
      this.showToastMessage('Unable to save a coding bundle without procedure codes. Please try again.');
      return;
    };
    const sub = this.codingBundleService.updateCodingBundles([this.editingBundle])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (index !== -1) {
            this.codingBundles[index] = this.editingBundle;
          } else {
            this.codingBundles.push(response.data[0]);
          }
          this.codingBundlesChanged.emit(this.codingBundles);
          this.isLoading = false;
          this.dialog.closeAll();
          this.showToastMessage('Coding settings updated successfully');
          sub.unsubscribe();
        },
        (error) => {
          this.isLoading = false;
          console.error('Error updating coding settings:', error);
          this.showToastMessage('There was an error updating your coding settings. Please try again.');
          sub.unsubscribe();
        }
      );
  }

  async deleteCodingBundle(id: string | number) {
    const confirmed = await this.showConfirmationDialog();

    if (typeof id === 'number') {
      this.codingBundles = this.codingBundles.filter((cb: any) => cb.id !== id);
      this.codingBundlesChanged.emit(this.codingBundles);
      return;
    }

    if (confirmed) {
      const sub = this.codingBundleService.deleteCodingBundle(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: any) => {
            this.codingBundles = this.codingBundles.filter((cb: any) => cb.id !== id);
            this.showToastMessage('Coding bundle deleted successfully');
            sub.unsubscribe();
          },
          (error) => {
            console.error('Error deleting coding bundle:', error);
            this.showToastMessage('There was an error deleting the coding bundle. Please try again.');
            sub.unsubscribe();
          }
        );
    }
  }

  validateJson() {
    try {
      JSON.parse(this.importCodingBundlesJson);
    } catch (error) {
      this.showToastMessage('Invalid JSON. Please check your JSON and try again.');
    }
  }

  async importCodingBundles() {
    try {
      const parsed = JSON.parse(this.importCodingBundlesJson);
      
      // Normalize to array: if it's a single object, wrap it in an array
      const json = Array.isArray(parsed) ? parsed : [parsed];

      if (json.length === 0) {
        this.showToastMessage('No coding bundles to import');
        return;
      }

      const validCodingBundles = json.filter((item: any) => item.label && item.procedureCodes && Array.isArray(item.procedureCodes) && item.procedureCodes.length > 0);
      if (validCodingBundles.length === 0) {
        this.showToastMessage('No valid coding bundles to import');
        return;
      }

      this.showToastMessage('Importing coding bundles...');

      const importRequests = [];
      for (let item of json) {
        item.id = 1;
        // check for existing coding bundle with the same label
        const existingCodingBundle = this.codingBundles.find((cb: any) => cb.label === item.label);
        if (existingCodingBundle) {
          const confirmAdd = await this.showConfirmationDialog(undefined, 'Coding bundle with the same title already exists. Do you want to add it anyway?', [], 'Ignore', 'Add Anyway', false);
          if (confirmAdd) {
            importRequests.push(this.codingBundleService.updateCodingBundles([item]));
          } else {
            continue;
          }
        } else {
          importRequests.push(this.codingBundleService.updateCodingBundles([item]));
        }
      }

      if (importRequests.length === 0) {
        this.showToastMessage('No valid coding bundles to import');
        return;
      }
      this.isLoading = true;
      const sub = forkJoin(importRequests)
        .pipe(takeUntil(this.destroy$))
        .subscribe(results => {
          for (let result of results) {
            this.codingBundles.push(result.data[0]);
          }
          this.codingBundlesChanged.emit(this.codingBundles);
          this.isLoading = false;
          this.showToastMessage('Coding bundles imported successfully');
          this.dialog.closeAll();
          sub.unsubscribe();
        });

      this.showToastMessage('Coding bundles imported successfully');
      this.dialog.closeAll();

    } catch (error) {
      // failed to parse JSON
      this.showToastMessage('Failed to parse JSON. Please check your JSON and try again.');
    }

  }


}